import { Icon } from "@legal-500/legal500-design-system";
import { EntityPicker, type Entity } from "../../components/EntityPicker";
import {
  EliteMetadataConfiguration,
  type OfficeContactPair,
} from "../../components/EliteMetadataConfiguration";
import styles from "./OnboardingTab.module.css";

export type MissingContactOffice = {
  office_id: number;
  city?: string | null;
};

export type MetadataFieldsEditorProps = {
  firmId: number | string | null;
  metadataQuantities: Record<string, number>;
  entityOptions: Record<string, Entity[]>;
  entityErrors: Record<string, string>;
  isLoadingEntities: boolean;
  fieldLabel: (key: string) => string;
  selections: Record<string, (number | string)[]>;
  onSelectionsChange: (key: string, values: (number | string)[]) => void;
  officeContactPairs: OfficeContactPair[];
  onOfficeContactPairsChange: (pairs: OfficeContactPair[]) => void;
  missingContactOffices?: MissingContactOffice[];
};

/**
 * Extracts the entity noun from a picker label (e.g. "Select Lawyers" -> "lawyer") so
 * requirement copy ("Select exactly 3 lawyers") can be generated from any field label.
 */
export function extractEntityNoun(label: string): string {
  const lowerLabel = label.toLowerCase();
  const prefixes = ["select ", "choose ", "pick "];
  let noun = lowerLabel;

  for (const prefix of prefixes) {
    if (lowerLabel.startsWith(prefix)) {
      noun = lowerLabel.substring(prefix.length);
      break;
    }
  }

  return noun.endsWith("s") ? noun.slice(0, -1) : noun;
}

/**
 * Renders the entity-picker fields for a package's `metadataQuantities` requirements —
 * the SAME controls used by both the initial onboarding/configuration flow and the
 * Metadata tab's inline re-edit flow on `SubscriptionDetail`, so the two never drift apart.
 */
export function MetadataFieldsEditor({
  firmId,
  metadataQuantities,
  entityOptions,
  entityErrors,
  isLoadingEntities,
  fieldLabel,
  selections,
  onSelectionsChange,
  officeContactPairs,
  onOfficeContactPairsChange,
  missingContactOffices = [],
}: MetadataFieldsEditorProps) {
  const metadataQuantitiesEntries = Object.entries(metadataQuantities);

  if (metadataQuantitiesEntries.length === 0) {
    return (
      <section className={styles.section}>
        <div className={styles.sectionContent}>
          <p>No entity selections required for this package.</p>
        </div>
      </section>
    );
  }

  return (
    <>
      {metadataQuantitiesEntries.map(([key, requiredCount]) => {
        if (key === "office_contact_pairs") {
          const isOptional = requiredCount === 0;
          return (
            <div key={key}>
              <EliteMetadataConfiguration
                firmId={firmId}
                initialPairs={officeContactPairs}
                isOptional={isOptional}
                onChange={onOfficeContactPairsChange}
              />
              {missingContactOffices.length > 0 && (
                <div className={styles.errorBanner} style={{ marginTop: "8px" }}>
                  <Icon icon="exclamation-circle" size="small" className={styles.errorIcon} />
                  <span className={styles.errorText}>
                    {missingContactOffices.length} office
                    {missingContactOffices.length !== 1 ? "s" : ""} excluded from sync (no contact
                    data):{" "}
                    {missingContactOffices
                      .map(office =>
                        office.city
                          ? `${office.city} (${office.office_id})`
                          : `Office ${office.office_id}`,
                      )
                      .join(", ")}
                  </span>
                </div>
              )}
            </div>
          );
        }

        const entities = entityOptions[key] || [];
        const selectedValues = selections[key] || [];
        const label = fieldLabel(key);
        const noun = extractEntityNoun(label);

        return (
          <section key={key} className={styles.section}>
            {key === "publication_edition_ids" && selectedValues.length === 0 && (
              <div className={styles.warningBanner}>
                <Icon icon="exclamation-circle" size="small" className={styles.warningIcon} />
                <span className={styles.warningText}>
                  Configuration required: publication edition
                </span>
              </div>
            )}
            <EntityPicker
              entities={entities}
              selectedValues={selectedValues}
              onChange={values => onSelectionsChange(key, values)}
              maxSelections={key === "publication_edition_ids" ? undefined : requiredCount}
              minSelections={key === "publication_edition_ids" ? 1 : undefined}
              label={label}
              helperText={
                key === "publication_edition_ids"
                  ? `Select one or more ${noun}s`
                  : `Select exactly ${requiredCount} ${noun}${requiredCount !== 1 ? "s" : ""}`
              }
              disabled={isLoadingEntities}
              emptyStateNoun={`${noun}s`}
              error={entityErrors[key]}
              columns={{ mobile: 1, tablet: 2, desktop: 3 }}
            />
          </section>
        );
      })}
    </>
  );
}
